import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../home/components/Footer';
import SEOHead from '../../components/feature/SEOHead';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const termosSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Termos de Uso | Carta do Eu Futuro',
  url: `${SITE_URL}/termos`,
  description: 'Termos de Uso do Carta do Eu Futuro. Leia as condições de uso da plataforma de desenvolvimento pessoal, PNL e gratidão.',
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Início', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Termos de Uso', item: `${SITE_URL}/termos` },
    ],
  },
};

const Termos = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-luxury-ivory">
      <SEOHead
        title="Termos de Uso | Carta do Eu Futuro"
        description="Termos de Uso do Carta do Eu Futuro. Leia as condições de uso da plataforma de desenvolvimento pessoal, PNL e gratidão."
        keywords="termos de uso, condições, carta do eu futuro, contrato"
        canonical="/termos"
        schema={termosSchema}
      />
      {/* Navbar */}
      <nav className="fixed top-0 left-0 w-full z-50 bg-luxury-black/90 backdrop-blur-md border-b border-gold-500/10">
        <div className="container mx-auto px-6 lg:px-16 flex items-center justify-between h-24">
          <Link to="/" className="flex items-center gap-3 cursor-pointer group">
            <img
              src="https://static.readdy.ai/image/9b313db6312c9605d9f9e3f145d3e29c/a68d70c6b66064870b0e9282c113321b.jpeg"
              alt="Carta do Eu Futuro"
              className="w-[72px] h-[72px] object-contain group-hover:scale-110 group-hover:drop-shadow-[0_0_14px_rgba(201,164,64,0.55)] transition-all duration-300"
            />
            <span className="font-['Playfair_Display'] text-lg font-semibold text-white">Carta do Eu Futuro</span>
          </Link>
          <div className="flex items-center gap-8">
            <Link to="/" className="font-['Montserrat'] text-xs text-white/50 hover:text-gold-400 transition-colors cursor-pointer tracking-wider uppercase">Início</Link>
            <Link to="/dashboard" className="font-['Montserrat'] text-xs text-white/50 hover:text-gold-400 transition-colors cursor-pointer tracking-wider uppercase">Minhas Cartas</Link>
            <Link to="/faq" className="font-['Montserrat'] text-xs text-white/50 hover:text-gold-400 transition-colors cursor-pointer tracking-wider uppercase">FAQ</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-luxury-black via-luxury-charcoal to-luxury-black relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 left-10 w-96 h-96 bg-gold-500 rounded-full blur-[120px]"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-gold-600 rounded-full blur-[120px]"></div>
        </div>
        <div className="container mx-auto px-6 lg:px-16 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold-500/10 border border-gold-500/20 mb-6">
              <i className="ri-file-text-line text-gold-400"></i>
              <span className="font-['Montserrat'] text-xs text-gold-400 tracking-wider uppercase">Termos e Condições</span>
            </div>
            <h1 className="font-['Playfair_Display'] text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">Termos de Uso</h1>
            <p className="font-['Montserrat'] text-sm text-white/60 leading-relaxed tracking-wide max-w-2xl mx-auto">Última atualização: Janeiro de 2025</p>
          </div>
        </div>
      </section>

      {/* Conteúdo */}
      <section className="py-20">
        <div className="container mx-auto px-6 lg:px-16">
          <div className="max-w-4xl mx-auto">
            <div className="mb-16">
              <p className="font-['Montserrat'] text-sm text-luxury-charcoal/70 leading-relaxed tracking-wide mb-4">
                Bem-vindo à <strong className="text-luxury-charcoal">Carta do Eu Futuro</strong>! Estes Termos de Uso regem o acesso e uso de nosso site e serviços. Ao utilizar nossa plataforma, você concorda em cumprir e estar vinculado a estes termos.
              </p>
              <p className="font-['Montserrat'] text-sm text-luxury-charcoal/70 leading-relaxed tracking-wide">
                Por favor, leia atentamente antes de utilizar nossos serviços. Se você não concordar com algum termo, não utilize nossa plataforma.
              </p>
            </div>

            <div className="mb-12 pb-12 border-b border-gold-500/10">
              <h2 className="font-['Playfair_Display'] text-3xl font-semibold text-luxury-charcoal mb-6">1. Aceitação dos Termos</h2>
              <p className="font-['Montserrat'] text-sm text-luxury-charcoal/70 leading-relaxed tracking-wide mb-4">
                Ao acessar e usar o site <strong className="text-luxury-charcoal">Carta do Eu Futuro</strong>, você aceita e concorda em estar vinculado a estes Termos de Uso e à nossa Política de Privacidade.
              </p>
            </div>

            <div className="mb-12 pb-12 border-b border-gold-500/10">
              <h2 className="font-['Playfair_Display'] text-3xl font-semibold text-luxury-charcoal mb-6">2. Descrição do Serviço</h2>
              <ul className="list-disc list-inside space-y-2 mb-6 font-['Montserrat'] text-sm text-luxury-charcoal/70 leading-relaxed tracking-wide ml-4">
                <li>Escrever uma carta pessoal para si mesmo no futuro</li>
                <li>Escolher quando deseja receber essa carta (1, 2, 3 ou 5 anos)</li>
                <li>Armazenar de forma segura suas reflexões, objetivos e mensagens</li>
                <li>Receber a carta por email na data escolhida</li>
                <li>Acessar suas cartas através de um painel pessoal</li>
              </ul>
            </div>

            <div className="mb-12 pb-12 border-b border-gold-500/10">
              <h2 className="font-['Playfair_Display'] text-3xl font-semibold text-luxury-charcoal mb-6">3. Cadastro e Conta de Usuário</h2>
              <h3 className="font-['Montserrat'] text-base font-semibold text-luxury-charcoal mb-4 tracking-wide">3.1 Elegibilidade</h3>
              <p className="font-['Montserrat'] text-sm text-luxury-charcoal/70 leading-relaxed tracking-wide mb-6">Você deve ter pelo menos 18 anos de idade para usar nossos serviços.</p>
              <h3 className="font-['Montserrat'] text-base font-semibold text-luxury-charcoal mb-4 tracking-wide">3.2 Informações Precisas</h3>
              <p className="font-['Montserrat'] text-sm text-luxury-charcoal/70 leading-relaxed tracking-wide mb-6">Você concorda em fornecer informações verdadeiras, precisas e completas durante o cadastro.</p>
              <h3 className="font-['Montserrat'] text-base font-semibold text-luxury-charcoal mb-4 tracking-wide">3.3 Segurança da Conta</h3>
              <p className="font-['Montserrat'] text-sm text-luxury-charcoal/70 leading-relaxed tracking-wide">Você é responsável por manter a confidencialidade de suas credenciais de acesso.</p>
            </div>

            <div className="mb-12 pb-12 border-b border-gold-500/10">
              <h2 className="font-['Playfair_Display'] text-3xl font-semibold text-luxury-charcoal mb-6">4. Pagamentos e Reembolsos</h2>
              <h3 className="font-['Montserrat'] text-base font-semibold text-luxury-charcoal mb-4 tracking-wide">4.1 Preços</h3>
              <ul className="list-disc list-inside space-y-2 mb-6 font-['Montserrat'] text-sm text-luxury-charcoal/70 leading-relaxed tracking-wide ml-4">
                <li><strong className="text-luxury-charcoal">Plano Gratuito:</strong> R$ 0,00 — Escreva sua carta e acesse-a no dashboard após o prazo escolhido</li>
                <li><strong className="text-luxury-charcoal">Plano Premium:</strong> R$ 97,00 (pagamento único) — Receba sua carta por email, notificação por WhatsApp, certificado digital e design exclusivo</li>
              </ul>
              <h3 className="font-['Montserrat'] text-base font-semibold text-luxury-charcoal mb-4 tracking-wide">4.2 Métodos de Pagamento</h3>
              <p className="font-['Montserrat'] text-sm text-luxury-charcoal/70 leading-relaxed tracking-wide mb-6">Aceitamos pagamentos via PIX, cartão de crédito e boleto bancário através do Asaas.</p>
              <h3 className="font-['Montserrat'] text-base font-semibold text-luxury-charcoal mb-4 tracking-wide">4.3 Política de Reembolso</h3>
              <ul className="list-disc list-inside space-y-2 font-['Montserrat'] text-sm text-luxury-charcoal/70 leading-relaxed tracking-wide ml-4">
                <li>Solicitação feita em até 7 dias após a compra (direito de arrependimento)</li>
                <li>Falha técnica que impeça a entrega da carta</li>
                <li>Cobrança duplicada ou erro no processamento</li>
              </ul>
            </div>

            <div className="mb-12 pb-12 border-b border-gold-500/10">
              <h2 className="font-['Playfair_Display'] text-3xl font-semibold text-luxury-charcoal mb-6">5. Uso Aceitável</h2>
              <p className="font-['Montserrat'] text-sm text-luxury-charcoal/70 leading-relaxed tracking-wide mb-4">Você concorda em NÃO usar nosso serviço para:</p>
              <ul className="list-disc list-inside space-y-2 font-['Montserrat'] text-sm text-luxury-charcoal/70 leading-relaxed tracking-wide ml-4">
                <li>Violar qualquer lei ou regulamento aplicável</li>
                <li>Infringir direitos de propriedade intelectual de terceiros</li>
                <li>Transmitir conteúdo ilegal, ofensivo, difamatório ou prejudicial</li>
                <li>Enviar spam, vírus ou código malicioso</li>
                <li>Tentar acessar áreas restritas do sistema</li>
              </ul>
            </div>

            <div className="mb-12 pb-12 border-b border-gold-500/10">
              <h2 className="font-['Playfair_Display'] text-3xl font-semibold text-luxury-charcoal mb-6">6. Propriedade Intelectual</h2>
              <p className="font-['Montserrat'] text-sm text-luxury-charcoal/70 leading-relaxed tracking-wide mb-4">Todo o conteúdo do site é propriedade da <strong className="text-luxury-charcoal">Carta do Eu Futuro</strong> ou de seus licenciadores. Você mantém todos os direitos sobre o conteúdo que cria (suas cartas).</p>
            </div>

            <div className="mb-12 pb-12 border-b border-gold-500/10">
              <h2 className="font-['Playfair_Display'] text-3xl font-semibold text-luxury-charcoal mb-6">7. Limitação de Responsabilidade</h2>
              <p className="font-['Montserrat'] text-sm text-luxury-charcoal/70 leading-relaxed tracking-wide">O serviço é fornecido "como está". Nossa responsabilidade total não excederá o valor pago por você pelo serviço.</p>
            </div>

            <div className="mb-12 pb-12 border-b border-gold-500/10">
              <h2 className="font-['Playfair_Display'] text-3xl font-semibold text-luxury-charcoal mb-6">8. Rescisão</h2>
              <p className="font-['Montserrat'] text-sm text-luxury-charcoal/70 leading-relaxed tracking-wide">Podemos suspender ou encerrar seu acesso ao serviço imediatamente, sem aviso prévio, em caso de violação destes Termos de Uso.</p>
            </div>

            <div className="mb-12 pb-12 border-b border-gold-500/10">
              <h2 className="font-['Playfair_Display'] text-3xl font-semibold text-luxury-charcoal mb-6">9. Lei Aplicável e Jurisdição</h2>
              <p className="font-['Montserrat'] text-sm text-luxury-charcoal/70 leading-relaxed tracking-wide">Estes Termos de Uso são regidos pelas leis da República Federativa do Brasil. Qualquer disputa será resolvida nos tribunais da comarca de São Paulo - SP.</p>
            </div>

            <div className="mb-12">
              <h2 className="font-['Playfair_Display'] text-3xl font-semibold text-luxury-charcoal mb-6">10. Contato</h2>
              <div className="bg-gradient-to-br from-gold-50 to-luxury-champagne/30 rounded-lg p-8 border border-gold-500/20">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <i className="ri-mail-line text-gold-600 text-lg mt-1"></i>
                    <div>
                      <p className="font-['Montserrat'] text-xs text-luxury-charcoal/50 uppercase tracking-wider mb-1">Email</p>
                      <p className="font-['Montserrat'] text-sm text-luxury-charcoal font-medium">contato@cartafuturo.org</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-customer-service-line text-gold-600 text-lg mt-1"></i>
                    <div>
                      <p className="font-['Montserrat'] text-xs text-luxury-charcoal/50 uppercase tracking-wider mb-1">Suporte</p>
                      <p className="font-['Montserrat'] text-sm text-luxury-charcoal font-medium">suporte@cartafuturo.org</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Termos;
